/**
 * Pairing Page (Simplified - Story 3.7.c Refactor)
 *
 * STRICT MVVM: Zero logic, only rendering based on ViewModel state
 * Single useEffect(onMount, [onMount]) for lifecycle
 *
 * SMART PATTERN:
 * - Reads scan context from Redux (publicScanSlice) instead of fetching
 * - NfcScanPage wrapper fetches scan context ONCE, stores in Redux
 * - PairingPage reads from Redux to avoid duplicate API calls
 *
 * SIMPLIFIED SCOPE (NfcScanPage handles routing now):
 * - ONLY handles unclaimed items → Shows PairingItemForm
 * - Shows success state after successful pairing
 * - If rendered for non-unclaimed item → Redirects back to NfcScanPage
 *
 * ROUTING HANDLED BY NfcScanPage:
 * - Stolen/lost → PairingItemStolenError (direct display)
 * - Claimed (owned) → Trophy Room redirect
 * - Claimed (other user) → PublicScanPage
 * - Invalid signature → PublicScanPage error
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useAppSelector } from '@/store/hooks';
import {
  selectScanContext,
  selectScanLoading,
  selectScanError,
} from '@/store/slices/publicScanSlice';
import {
  isUnclaimedItem,
} from '@/domain/types/ViewerNfcScanContext';
import { usePairingViewModel } from '@/viewModels/usePairingViewModel';
import { PairingItemForm } from '@/ui/components/PairingItemForm';
import { PairingItemSuccess } from '@/ui/components/PairingItemSuccess';
import { ErrorState, PairingFormSkeleton } from '@/ui/components';
import { AppLayout } from '@/ui/layouts';
import { parseNfcIdentifier } from '@/utils/nfcUrlParser';

/**
 * PairingPage Component
 *
 * Displays NFC product pairing interface with scan context-based routing.
 * Uses AppLayout for consistent header/footer across all states.
 */
export default function PairingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { productId, itemId, signature } = useParams<{
    productId: string;
    itemId: string;
    signature: string;
  }>();
  const { state, actions, computed, onMount } = usePairingViewModel(navigate);

  // SMART PATTERN: Read scan context from Redux (shared with NfcScanPage wrapper)
  const scanContext = useAppSelector(selectScanContext);
  const scanLoading = useAppSelector(selectScanLoading);
  const scanError = useAppSelector(selectScanError);

  // Single useEffect for lifecycle (MVVM requirement)
  useEffect(onMount, [onMount]);

  // Fallback: If PairingPage rendered for non-unclaimed item, redirect back to NfcScanPage
  // This should never happen with proper routing in NfcScanPage, but handles edge cases
  useEffect(() => {
    if (scanContext && !isUnclaimedItem(scanContext) && productId && itemId && signature) {
      // @ts-expect-error - Union type narrowing needed for InvalidSignatureContext
      console.warn('[PairingPage] Unexpected scanContext type:', scanContext.type, '- redirecting to NfcScanPage');
      navigate(`/nfc/${productId}/${itemId}/${signature}`);
    }
  }, [scanContext, navigate, productId, itemId, signature]);

  // Show loading skeleton during scan context fetch (from NfcScanPage wrapper)
  if (scanLoading || !scanContext) {
    return (
      <AppLayout>
        <PairingFormSkeleton />
      </AppLayout>
    );
  }

  // Error state - couldn't load scan context
  if (scanError) {
    return (
      <AppLayout>
        <ErrorState
          message={t(
            'pairing.errors.unableToLoad',
            'Unable to load NFC scan context. Please try scanning again.'
          )}
          // No onRetry - user must re-scan NFC tag physically
        />
      </AppLayout>
    );
  }

  // SIMPLIFIED PATTERN: Only handles unclaimed items and success state
  // All other routing (stolen/claimed/invalid) handled by NfcScanPage

  // 1. Immediate pairing success (after pairing action)
  if (state.status === 'success' && state.product) {
    return (
      <AppLayout>
        <PairingItemSuccess
          productImage={state.product.imageUrl}
          brandName="ANYW3AR"
          productName={state.product.title}
          nfcId={state.nfcUrl ? parseNfcIdentifier(state.nfcUrl) : ''}
          productNumber={{ current: state.itemNumber ?? 0, total: state.product?.editionSize ?? 0 }}
          productSize="N/A" // TODO: Get from API
          onUnpair={actions.unpairItem}
          onGoToCollection={actions.goToCollection}
        />
      </AppLayout>
    );
  }

  // 5. Unclaimed item - show pairing form (main happy path)
  if (isUnclaimedItem(scanContext)) {
    const productInfo = scanContext.productInfo;

    return (
      <AppLayout>
        <PairingItemForm
          productTitle={productInfo.title}
          productImage={productInfo.imageUrl}
          nfcId={parseNfcIdentifier(scanContext.nfcId)}
          nfcVerified={true}
          codeFirst={state.codeFirst}
          codeSecond={state.codeSecond}
          onCodeFirstChange={actions.setCodeFirst}
          onCodeSecondChange={actions.setCodeSecond}
          onValidate={actions.pairImmediately}
          loading={state.loading}
          disabled={!computed.canPairImmediately || state.loading}
          error={state.error?.message}
          codeRequired={false}
        />
      </AppLayout>
    );
  }

  // Fallback: Should never reach here (useEffect redirects to NfcScanPage for non-unclaimed items)
  return null;
}
